import React from "react"
import { Box, Flex, Image, PseudoBox, Text } from "@chakra-ui/core"
import { bp } from "../../utils/MediaQueries"
import {
  BikeIcon,
  ChatIcon,
  TrikeIcon,
  openZendeskChat,
  useFittingConsultation,
} from "./shared"

type CompactFittingOptionProps = {
  title: string
  icon: React.ReactNode
  onClick: () => void
}

const HorizontalCloseButton = ({ onClick }: { onClick: () => void }) => (
  <Box
    as="button"
    aria-label="Close fitting consultation widget"
    flexShrink={0}
    w={bp("22px", "26px")}
    h={bp("22px", "26px")}
    borderRadius="6px"
    border="1px solid rgba(255,255,255,0.55)"
    bg="transparent"
    display="flex"
    alignItems="center"
    justifyContent="center"
    cursor="pointer"
    onClick={onClick}
  >
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <path
        d="M2 2L10 10M10 2L2 10"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  </Box>
)

const CompactFittingOption = ({
  title,
  icon,
  onClick,
}: CompactFittingOptionProps) => (
  <PseudoBox
    as="button"
    w="100%"
    h={bp("44px", "54px")}
    px={bp("8px", "12px")}
    py={bp("5px", "8px")}
    bg="#f4f8f9"
    border="1px solid #e6ebee"
    borderRadius={bp("10px", "14px")}
    textAlign="left"
    cursor="pointer"
    transition="border-color 0.2s ease, box-shadow 0.2s ease"
    _hover={{
      borderColor: "primary",
      boxShadow: "0 4px 12px rgba(0, 196, 209, 0.12)",
    }}
    onClick={onClick}
  >
    <Flex align="center" h="100%">
      <Flex
        w={bp("36px", "48px")}
        h={bp("28px", "38px")}
        borderRadius="11px"
        bg="rgba(0, 196, 209, 0.12)"
        align="center"
        justify="center"
        flexShrink={0}
        transform={bp("scale(0.85)", "none")}
      >
        {icon}
      </Flex>
      <Text
        flex="1"
        ml={bp("10px", "12px")}
        fontSize={bp("12px", "15px")}
        fontWeight="600"
        color="#14181b"
        lineHeight="1.2"
      >
        {title}
      </Text>
      <Text fontSize={bp("15px", "18px")} color="primary" flexShrink={0}>
        →
      </Text>
    </Flex>
  </PseudoBox>
)

const HorizontalFittingConsultationWidget = () => {
  const { isVisible, dismissWidget, handleFittingSelect } =
    useFittingConsultation()

  if (!isVisible) return null

  return (
    <>
      <Box
        position="relative"
        w={bp("280px", "400px")}
        maxW={bp("280px", "400px")}
        flexShrink={0}
        pointerEvents="auto"
        mb={bp("0.5rem", "0")}
      >
        <Box
          bg="white"
          borderRadius={bp("10px", "14px")}
          border="1px solid #e6ebee"
          boxShadow="0 12px 40px rgba(18, 25, 35, 0.12)"
          overflow="hidden"
        >
          <Flex
            bg="primary"
            px={bp("0.75rem", "20px")}
            py={bp("8px", "12px")}
            align="center"
            borderTopLeftRadius={bp("10px", "14px")}
            borderTopRightRadius={bp("10px", "14px")}
          >
            <Box w={bp("22px", "26px")} flexShrink={0} />
            <Text
              flex="1"
              color="white"
              fontSize={bp("8px", "11px")}
              fontWeight="700"
              letterSpacing={bp("1px", "1.5px")}
              textAlign="center"
              lineHeight="1.35"
              px={bp("4px", "8px")}
            >
              GET YOUR PERSONAL FITTING CONSULTATION
            </Text>
            <HorizontalCloseButton onClick={dismissWidget} />
          </Flex>

          <Flex
            px={bp("0.75rem", "20px")}
            pt={bp("0.75rem", "18px")}
            pb={bp("0.5rem", "16px")}
            align="stretch"
            direction={bp("column", "row")}
          >
            <Box flex="1" pr={bp(0, "16px")} pb={bp("0.5rem", 0)}>
              <Text
                display={bp("block", "none")}
                fontSize={bp("14px", "24px")}
                fontWeight="700"
                color="#14181b"
                letterSpacing="-0.6px"
                lineHeight="1.15"
                whiteSpace="nowrap"
              >
                No chatbots. No quizzes.
              </Text>
              <Text
                display={bp("none", "block")}
                fontSize={bp("16px", "24px")}
                fontWeight="700"
                color="#14181b"
                letterSpacing="-0.6px"
                lineHeight="1.15"
              >
                No chatbots.
                <br />
                No quizzes.
              </Text>

              <Flex mt={bp("8px", "14px")} align="center">
                <Image
                  src="/images/Dustin.jpg"
                  alt="Dustin, Head Fit Specialist"
                  w={bp("28px", "36px")}
                  h={bp("28px", "36px")}
                  borderRadius="50%"
                  objectFit="cover"
                  flexShrink={0}
                />
                <Text
                  fontSize={bp("10px", "12px")}
                  color="#5a6a72"
                  ml={bp("8px", "10px")}
                  lineHeight="1.45"
                >
                  Just honest advice from{" "}
                  <Text as="span" color="primary" fontWeight="700">
                    Dustin
                  </Text>{" "}
                  — our real bike expert.
                </Text>
              </Flex>
            </Box>

            <Flex
              direction="column"
              flex="1"
              gridGap={bp("5px", "8px")}
              minW={bp("auto", "190px")}
            >
              <CompactFittingOption
                title="E-Trike Fitting"
                icon={<TrikeIcon />}
                onClick={() => handleFittingSelect("E-Trike Fitting")}
              />
              <CompactFittingOption
                title="E-Bike Fitting"
                icon={<BikeIcon />}
                onClick={() => handleFittingSelect("E-Bike Fitting")}
              />
            </Flex>
          </Flex>

          <Box
            borderTop="1px solid #e6ebee"
            mx={bp("0.75rem", "20px")}
            pt={bp("8px", "12px")}
            pb={bp("10px", "14px")}
          >
            <Flex
              as="button"
              align="center"
              cursor="pointer"
              bg="transparent"
              border="none"
              onClick={openZendeskChat}
            >
              <ChatIcon />
              <Text
                fontSize={bp("10px", "12px")}
                fontWeight="600"
                color="#14181b"
                ml="10px"
              >
                Real Advice. Real People.
              </Text>
            </Flex>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default HorizontalFittingConsultationWidget
